import React, { useEffect, useRef, useState } from 'react';
import { apiService, getApiErrorMessage, MetadataExport, WorkflowDeliveryArtifact } from '../services/api';
import { ArtifactLinkTarget } from '../navigation/resultLinks';
import { getExportDeliveryAuthority } from '../utils/exportDelivery';
import { useTabActivity } from '../contexts/TabActivityContext';
import { usePassiveApiActivity } from '../hooks/usePassiveApiActivity';
import { isInProgressStatus } from '../utils/realtimeStreamEligibility';
import ExportJobCardCompact from './ExportJobCardCompact';

const WAITING_STATES = new Set(['waiting_for_source', 'pending', 'preparing']);
const ARTIFACT_LABELS = { all: 'Full JSON', matches: 'Matches JSON', search_results: 'Search results JSON' };

/** One landing surface for a source intent and its eventual canonical export. */
const WorkflowArtifactPanel: React.FC<{
  target: ArtifactLinkTarget;
  onDownloadConsumed: () => void;
  onExportResolved?: (exportId: string) => void;
}> = ({ target, onDownloadConsumed, onExportResolved }) => {
  const { isActive } = useTabActivity();
  const [job, setJob] = useState<MetadataExport | null>(null);
  const [artifact, setArtifact] = useState<WorkflowDeliveryArtifact | null>(null);
  const canObserve = usePassiveApiActivity(Boolean(
    (job && isInProgressStatus(job.status)) || (artifact && WAITING_STATES.has(artifact.state)),
  ));
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
  const consumedDownloadRef = useRef(false);
  const resolvedCallbackRef = useRef(onExportResolved);
  resolvedCallbackRef.current = onExportResolved;
  const consumedCallbackRef = useRef(onDownloadConsumed);
  consumedCallbackRef.current = onDownloadConsumed;

  useEffect(() => {
    if (!isActive || !canObserve) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const load = async () => {
      try {
        let nextJob: MetadataExport | null = null;
        let nextArtifact: WorkflowDeliveryArtifact | null = null;
        // A completed export survives source/snapshot expiry. Only a genuine
        // missing-job response may fall back to the source admission intent.
        if (target.exportId) {
          try { nextJob = await apiService.getExportStatus(target.exportId); }
          catch (loadError: any) {
            if (loadError?.response?.status !== 404) throw loadError;
            if (!target.sourceKind || !target.sourceId) throw loadError;
          }
        }
        if (cancelled) return;
        if (!nextJob && target.sourceKind && target.sourceId) {
          const delivery = await apiService.getWorkflowDelivery(target.sourceKind, target.sourceId);
          if (cancelled) return;
          nextArtifact = delivery.artifacts.find((item) => item.scope === target.scope) || null;
          if (!nextArtifact) throw new Error('This JSON artifact is not available for the selected source.');
          if (nextArtifact.export_id) {
            if (nextArtifact.export_id !== target.exportId && resolvedCallbackRef.current) {
              resolvedCallbackRef.current(nextArtifact.export_id);
              return;
            }
            try { nextJob = await apiService.getExportStatus(nextArtifact.export_id); }
            catch (loadError: any) {
              if (loadError?.response?.status !== 404 || !WAITING_STATES.has(nextArtifact.state)) throw loadError;
            }
          }
        }
        if (cancelled) return;
        setJob(nextJob);
        setArtifact(nextArtifact);
        setError(null);
        setLoading(false);
        const pending = nextJob ? ['pending', 'processing'].includes(nextJob.status)
          : nextArtifact && WAITING_STATES.has(nextArtifact.state);
        if (pending) timer = setTimeout(() => { void load(); }, 2500);
      } catch (loadError) {
        if (cancelled) return;
        setLoading(false);
        setError(getApiErrorMessage(loadError, 'This JSON artifact could not be loaded.'));
      }
    };
    void load();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [isActive, canObserve, refresh, target.exportId, target.scope, target.sourceId, target.sourceKind]);

  useEffect(() => {
    if (!isActive || !canObserve || !target.download || !job || consumedDownloadRef.current) return;
    if (getExportDeliveryAuthority(job) !== 'authenticated_download') return;
    consumedDownloadRef.current = true;
    consumedCallbackRef.current();
    void apiService.downloadExport(job.export_id).catch((downloadError) => {
      if (mountedRef.current) setError(getApiErrorMessage(downloadError, 'The JSON download failed. Use Download Export to try again.'));
    });
  }, [isActive, canObserve, job, target.download]);

  const displayedScope = job?.export_type === 'search_snapshot' ? 'search_results'
    : job?.export_params?.result_scope === 'matches' ? 'matches'
      : job?.export_params?.result_scope === 'all' ? 'all' : target.scope;

  return <section aria-label="Requested JSON artifact" className="jobs-tab-content">
    <h3>{ARTIFACT_LABELS[displayedScope]}</h3>
    <p>Historical snapshot captured when this export was generated.</p>
    {loading && <p role="status">Loading JSON artifact…</p>}
    {error && <div role="alert"><p>{error}</p><button type="button" onClick={() => setRefresh((value) => value + 1)}>Retry loading artifact</button></div>}
    {!job && artifact && <p role={artifact.state === 'failed' ? 'alert' : 'status'}>
      {artifact.error_message || (artifact.state === 'waiting_for_source' ? 'Waiting for processing to finish.'
        : WAITING_STATES.has(artifact.state) ? 'Preparing your JSON download…'
          : artifact.state === 'not_applicable' ? 'This source does not have a matches JSON artifact.'
            : artifact.state === 'failed' ? 'The JSON artifact could not be prepared.'
              : 'This JSON artifact is unavailable.')}
    </p>}
    {artifact?.captured_at && <p>Captured {new Date(artifact.captured_at).toLocaleString()}</p>}
    {job && <ExportJobCardCompact job={job} />}
  </section>;
};

export default WorkflowArtifactPanel;
